/**
 * Custom hook for managing farm location editing.
 * Extracted from FarmDetailsModal.tsx.
 */

import { useState, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { apiClient } from "../../../api/client/axiosClient";
import { ENDPOINTS } from "../../../api/endpoints";
import {
  useCity,
  useCorregimiento,
  useVereda,
  useRegion,
  useRegions,
  useCities,
  useVeredasByCity,

  LocationDto,
} from "../../../api/hooks/location.hooks";

interface UseFarmLocationEditorProps {
  farmLocation: any;
  organizationId: string;
}

interface UseFarmLocationEditorResult {
  // State
  isEditingLocation: boolean;
  setIsEditingLocation: (value: boolean) => void;
  isUpdatingLocation: boolean;
  forceRefresh: number;
  // Location data
  cityId: string;
  corregimientoId: string;
  veredaId: string;
  regionId: string;
  cityName: string;
  corregimientoName: string;
  veredaName: string;
  regionName: string;
  // Selected values
  selectedRegionId: string;
  selectedCityId: string;
  selectedVeredaId: string;
  // Lists for selects
  regions: LocationDto[];
  cities: LocationDto[];
  veredas: LocationDto[];
  // Setters
  setSelectedRegionId: (value: string) => void;
  setSelectedCityId: (value: string) => void;
  setSelectedVeredaId: (value: string) => void;
  // Handlers
  handleSaveFarmLocationChanges: () => Promise<void>;
}

export function useFarmLocationEditor({
  farmLocation,
  organizationId,
}: UseFarmLocationEditorProps): UseFarmLocationEditorResult {
  const queryClient = useQueryClient();

  const [isEditingLocation, setIsEditingLocation] = useState(false);
  const [isUpdatingLocation, setIsUpdatingLocation] = useState(false);
  const [forceRefresh, setForceRefresh] = useState(0);

  // Get location details if available
  const cityId = farmLocation?.cityId || "";
  const corregimientoId = farmLocation?.corregimientoId || "";
  const veredaId = farmLocation?.veredaId || "";

  const [selectedRegionId, setSelectedRegionId] = useState<string>("");
  const [selectedCityId, setSelectedCityId] = useState<string>(cityId);
  const [selectedVeredaId, setSelectedVeredaId] = useState<string>(veredaId);

  // Fetch location data
  const { data: cityResponse } = useCity(cityId, {
    queryKey: ["city", cityId, forceRefresh],
    enabled: !!cityId,
  });

  const { data: corregimientoResponse } = useCorregimiento(corregimientoId, {
    queryKey: ["corregimiento", corregimientoId, forceRefresh],
    enabled: !!corregimientoId,
  });

  const { data: veredaResponse } = useVereda(veredaId, {
    queryKey: ["vereda", veredaId, forceRefresh],
    enabled: !!veredaId,
  });

  const regionId = cityResponse?.data?.regionId || "";
  const { data: regionResponse } = useRegion(regionId, {
    queryKey: ["region", regionId, forceRefresh],
    enabled: !!regionId,
  });

  const cityName = cityResponse?.data?.name || "";
  const corregimientoName = corregimientoResponse?.data?.name || "";
  const veredaName = veredaResponse?.data?.name || "";
  const regionName = regionResponse?.data?.name || "";

  // Get lists for selects
  const { data: regionsResponse } = useRegions();
  const { data: citiesResponse } = useCities(selectedRegionId);
  const { data: veredasResponse } = useVeredasByCity(selectedCityId);

  const regions = regionsResponse?.data || [];
  const cities = citiesResponse?.data || [];
  const veredas = veredasResponse?.data || [];

  // Initialize selected values when data is available
  useEffect(() => {
    if (regionId && !selectedRegionId) {
      setSelectedRegionId(regionId);
    }
  }, [regionId, selectedRegionId]);

  // Reset vereda when region or city changes
  useEffect(() => {
    if (!selectedRegionId || !selectedCityId) {
      setSelectedVeredaId("");
    }
  }, [selectedRegionId, selectedCityId]);

  // Handle saving farm location changes
  const handleSaveFarmLocationChanges = async () => {
    if (!farmLocation || !organizationId) {
      console.error("No farm location or organization ID available");
      return;
    }

    setIsUpdatingLocation(true);
    try {
      // Get the corregimientoId from the selected vereda
      const selectedVereda = veredas.find((v) => v.id === selectedVeredaId);
      const corregimientoId = selectedVereda?.corregimientoId;

      if (!corregimientoId) {
        throw new Error(
          "No se pudo obtener el corregimiento de la vereda seleccionada",
        );
      }

      // Prepare update data with correct order
      const updateData = {
        id: farmLocation.id,
        organizationId: farmLocation.organizationId,
        cityId: selectedCityId,
        corregimientoId: corregimientoId,
        veredaId: selectedVeredaId,
        latitude: farmLocation.latitude,
        longitude: farmLocation.longitude,
        elevation: farmLocation.elevation,
        geometry: farmLocation.geometry,
        isActive: farmLocation.isActive,
        area: (farmLocation as any).area || 0,
      };

      const updateJson = JSON.stringify(updateData);

      await apiClient.put(
        ENDPOINTS.farmLocations.update(organizationId, farmLocation.id),
        updateJson,
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      await queryClient.invalidateQueries();
      setForceRefresh((prev) => prev + 1);
      setIsEditingLocation(false);
    } catch (error) {
      console.error("Error saving location:", error);
    } finally {
      setIsUpdatingLocation(false);
    }
  };

  return {
    isEditingLocation,
    setIsEditingLocation,
    isUpdatingLocation,
    forceRefresh,
    cityId,
    corregimientoId,
    veredaId,
    regionId,
    cityName,
    corregimientoName,
    veredaName,
    regionName,
    selectedRegionId,
    selectedCityId,
    selectedVeredaId,
    regions,
    cities,
    veredas,
    setSelectedRegionId,
    setSelectedCityId,
    setSelectedVeredaId,
    handleSaveFarmLocationChanges,
  };
}
